"use client";

import { Dialog } from "@base-ui/react/dialog";
import { XIcon } from "lucide-react";
import ArrowIcon from "@/components/icons/arrow";
import { Button } from "@/components/ui/button";
import ExtendedImage from "@/components/ui/extended-image";
import type { Ebook } from "@/types/academy";

export interface SearchEbookCardProps {
  ebook: Ebook;
}

const SearchEbookCard = ({ ebook }: SearchEbookCardProps) => {
  const title = ebook.name;
  const summary = ebook.summary;
  const image = ebook.image;

  const handleDownload = () => {
    const fileUrl = ebook.file || "#";
    const link = document.createElement("a");
    link.href = fileUrl;
    link.target = "_blank";
    link.download = `${title}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog.Root>
      <li className="border h-50 max-w-full border-gray-300 rounded-2xl p-2 gap-2 flex flex-row justify-end relative overflow-hidden bg-white list-none">
        {image ? (
          <ExtendedImage
            alt={title}
            className="rounded-2xl object-cover"
            fill={true}
            src={image}
          />
        ) : null}

        <div className="flex flex-col justify-center relative z-10 w-1/2">
          <h2 className="text-lg leading-5.5 font-bold">{title}</h2>
          <h2 className="text-[12px] leading-4.5 font-medium">{summary}</h2>

          <Dialog.Trigger className="mt-4 appearance-none border-none bg-transparent p-0 m-0 text-left w-fit cursor-pointer">
            <div className="bg-brand-blue-600 hover:bg-brand-blue-700 text-white rounded-full px-4 py-2 cursor-pointer">
              <span className="inline-flex w-fit shrink-0 break-keep items-center">
                Detayli Bilgi <ArrowIcon className="stroke-white ml-1" />
              </span>
            </div>
          </Dialog.Trigger>
        </div>
      </li>

      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-60 bg-black/40 backdrop-blur-sm transition-opacity" />
        <Dialog.Popup className="fixed top-1/2 left-1/2 z-60 flex w-[90vw] max-w-md -translate-x-1/2 -translate-y-1/2 flex-col items-center rounded-3xl bg-white shadow-2xl outline-none max-h-[90vh] overflow-y-auto">
          <Dialog.Title className="border-b border-gray-200 w-full text-center text-xl font-bold text-gray-900 px-6 py-4 relative">
            {title}
            <Dialog.Close className="absolute right-4 top-4 rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 cursor-pointer">
              <XIcon className="size-5" />
            </Dialog.Close>
          </Dialog.Title>

          <div className="w-full p-6 flex flex-col gap-4">
            {image ? (
              <div className="flex justify-center border border-gray-200 rounded-2xl w-full p-2">
                <ExtendedImage
                  alt={title}
                  className="my-2 h-48 w-32 shrink-0 rounded-xl object-contain"
                  height={192}
                  src={image}
                  width={128}
                />
              </div>
            ) : null}

            <h3 className="text-lg font-bold text-gray-900">
              e-Kitap İçeriği:
            </h3>
            <Dialog.Description className="mb-4 text-sm font-medium leading-5 text-gray-600">
              {summary}
            </Dialog.Description>
          </div>

          <div className="border-t border-gray-200 py-4 px-6 w-full">
            <Button
              className="w-full h-12 rounded-full text-base font-bold cursor-pointer"
              onClick={handleDownload}
            >
              Ücretsiz İndir
            </Button>
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default SearchEbookCard;
